// Components
import { CreateProject } from '@/pigeonline/wrapper';
import DatasetLayout from '@/components/Layouts/DatasetLayout.vue';
import Spinner from '@/components/UI/Spinner.vue';
import MessageBlock from '@/components/UI/Message/MessageBlock.vue';

// Icons
import UploadCloudIcon from '@/assets/icons/Datasets/upload-cloud.svg';

// Mixins
import DatasetMixin from '@/components/Dataset/Mixins/datasetWizardMixin.js';
import DatasetDetailsMixin from '@/utils/mixins/datasetDetailsMixin';
import UserMixin from '@/utils/mixins/userMixin.js';

export default {
  name: 'DatasetUploader',
  components: {
    CreateProject,
    DatasetLayout,
    DatasetUploaderReupload: () => import('./DatasetUploaderReupload.vue'),
    Spinner,
    MessageBlock,
    UploadCloudIcon,
  },
  mixins: [DatasetMixin, DatasetDetailsMixin, UserMixin],
  props: {
    datasetWizardStatus: {
      default: () => {},
      type: Object,
    },
  },
  data() {
    return {
      currDatasetProjectId: null,
      errMsg: {
        dataSheet: null,
        file: null,
        fileName: null,
      },
      form: {
        file: null,
        sheets: null,
        selectedSheet: null,
        selectedCodebookSheet: null,
        delimiters: null,
        encodings: null,
        delimiter: null,
        encoding: null,
      },
      reform: {
        file: null,
      },
      uploadResponse: null,
      isCodebookUploaded: false,
      isDataChanged: false,
      isExcelMultiSheetUploaded: false,
      isCSVUploaded: false,
      isLoading: false,
      isShowingReupload: false,
      isCodebookErr: false,
      // check text AI status
      counter: 0,
      timer: null,
      begunUpload: false,
    };
  },
  mounted() {
    // fix firefox bug where file upload focus is not recognized
    const fileInput = document.getElementById('dataset-file-upload');
    fileInput.addEventListener('focus', () => {
      fileInput.classList.add('has-focus');
    });
    fileInput.addEventListener('blur', () => {
      fileInput.classList.remove('has-focus');
    });
  },
  activated() {
    // if dataset exists set file name and disable upload
    if (Object.keys(this.dataset).length > 0) {
      this.$refs.file.disabled = true;
      this.$refs['file-upload-label'].innerHTML = this.dataset.name;
      this.$refs['file-label'].classList.add('disabled');
    }
  },
  deactivated() {
    // reset data change value
    this.isDataChanged = false;
    if (!this.dataset) {
      this.$destroy();
    }
  },
  computed: {
    textAIStatus() {
      let isComplete = [];
      for (let a = 0; a < this.clientTextQuestions.length; a++) {
        if (
          this.clientTextQuestions[a]['text_preprocessing'] &&
          this.clientTextQuestions[a]['text_preprocessing']['processing']
        ) {
          return 'processing';
        }
        if (
          this.clientTextQuestions[a]['text_preprocessing'] &&
          this.clientTextQuestions[a]['text_preprocessing']['error']
        ) {
          return 'error';
        }
        if (
          this.clientTextQuestions[a]['text_preprocessing'] &&
          this.clientTextQuestions[a]['text_preprocessing']['complete']
        ) {
          isComplete.push(a);
        }
      }
      if (isComplete.length && isComplete.length == this.clientTextQuestions.length) {
        return 'complete';
      }
      return '';
    },
  },
  methods: {
    clearErrors() {
      this.errMsg = {
        dataSheet: null,
        file: null,
        fileName: null,
      };
    },
    validate() {
      let firstErr = null;
      if (!this.form.file) {
        this.errMsg.file = 'Error: No file uploaded';
        this.setDeleteIncompleteUpload(this.currDatasetProjectId);
        firstErr = 'file';
      } else if (
        this.datasetProject.file_type !== 'sav' &&
        this.datasetProject.file_type !== 'xls' &&
        this.datasetProject.file_type !== 'xlsx' &&
        this.datasetProject.file_type !== 'csv' &&
        this.datasetProject.file_type !== 'ods'
      ) {
        this.errMsg.file = 'Error: Wrong file type selected';
        this.setDeleteIncompleteUpload(this.currDatasetProjectId);
        firstErr = 'file';
      }
      if (!this.datasetProject.name || this.datasetProject.name.length === 0) {
        this.errMsg.fileName = 'Error: File name required';
        if (!firstErr) firstErr = 'file-name';
      }
      if (
        (this.datasetProject.file_type === 'xls' || this.datasetProject.file_type === 'xlsx') &&
        this.isExcelMultiSheetUploaded
      ) {
        if (!this.form.selectedSheet) {
          this.errMsg.dataSheet = 'Error: No data sheet selected';
          if (!firstErr) firstErr = 'select-sheet';
        }
      }
      if (firstErr) this.$refs[firstErr].focus();
      return !(firstErr && firstErr.length > 0);
    },
    /***********
     *
     * Form related helper methods
     *
     ***********/
    async onFileChange() {
      // Now just disabling the form after an upload, the below was to allow the file to be switched but they can always more safely refresh
      // reset error
      // this.clearErrors();
      // this.form.sheets = null;
      // this.form.selectedSheet = null;
      // this.form.selectedCodebookSheet = null;
      // this.isExcelMultiSheetUploaded = false;
      this.begunUpload = true;

      // set file details
      this.form.file = this.$refs.file.files[0];
      this.datasetProject.name = this.$refs.file.files[0].name.substring(
        0,
        this.$refs.file.files[0].name.lastIndexOf('.'),
      );
      this.datasetProject.file_type = this.$refs.file.files[0].name.substring(
        this.$refs.file.files[0].name.lastIndexOf('.') + 1,
        this.$refs.file.files[0].name.length,
      );

      // change label to file name
      const $label = this.$refs['file-upload-label'];
      $label.innerHTML = this.$refs.file.files[0].name;

      // if it is an excel sheet, user should select what sheet to use
      if (
        this.datasetProject.file_type === 'xls' ||
        this.datasetProject.file_type === 'xlsx' ||
        this.datasetProject.file_type === 'ods'
      ) {
        await this.processExcel();
      } else if (this.datasetProject.file_type === 'csv') {
        await this.processCSV();
      } else if (this.datasetProject.file_type === 'sav') {
        this.isCodebookUploaded = true;
      } else {
        this.isExcelMultiSheetUploaded = false;
        this.selectedSheet = null;
        this.selectedCodebookSheet = null;
      }
    },
    async onSecondFileChange() {
      // TODO sync with multi-slide logic, and other csv stuff
      // TODO what to do about "datasetProject details, will there be multiple of these or what? 1.10.0!"
      this.isLoading = true;
      this.reform.file = this.$refs.refile.files[0];
      let formData = new FormData();
      formData.append('file', this.reform.file);
      try {
        const response = await this.$services.DATASETS_SERVICE.secondUpload(
          formData,
          this.dataset._id.$oid,
        );
        const dataset = await this.$services.DATASETS_SERVICE.dataset(this.dataset._id.$oid);
        await this.setDataset(dataset);
        this.startTimer();
        return response.data;
      } catch (e) {
        this.setDeleteIncompleteUpload(this.currDatasetProjectId);
        alert('There was an error uploading the file. Please try again');
        this.startTimer();
        throw new Error('DatasetUploader:upload ' + e.message);
      }
    },

    onNameChange() {
      if (this.form.selectedSheet == null && this.dataset.sheetname) {
        this.form.selectedSheet = this.dataset.sheetname;
      }
      if (this.form.selectedSheet == null && this.dataset.sheet_name) {
        this.form.selectedSheet = this.dataset.sheet_name;
      }
      if (this.form.selectedCodebookSheet == null && this.dataset.code_book_sheet_name) {
        this.form.selectedCodebookSheet = this.dataset.code_book_sheet_name;
      }

      this.isDataChanged = true;

      this.errMsg.fileName = null;
      this.$refs['file-upload-label'].innerHTML = this.datasetProject.name;
    },
    async onRename() {
      if (this.datasetProject && this.datasetProject.name) {
        await this.edit();
        await this.saveDatasetProject(this.datasetProject);
        this.setActiveSection('DatasetCleaner');
      }
    },
    onSelectSheetChange() {
      if (this.form.selectedSheet) this.errMsg.dataSheet = null;
    },
    /***********
     *
     * Dataset project methods
     *
     ***********/
    async datasetProjectCreated(project) {
      this.isLoading = true;
      this.currDatasetProjectId = project.id;
      // if the project lacks a dataset_id, and dataset._id does not exist,
      // it could be waiting for dataset details like excel sheets or csv files
      if (!project.data_set_id && !Object.keys(this.dataset).length) {
        await this.onFileChange();
      }
      await this.setDatasetProject(project);
      if (!this.validate()) return;

      // upload, or edit the dataset if it was a single excel sheet
      const response =
        Object.keys(this.dataset).length > 0 ? await this.edit() : await this.uploadDataset();
      this.uploadResponse = response;

      // get dataset id
      const datasetId =
        response && response.data_set_id ? response.data_set_id.$oid : this.dataset._id.$oid;
      await this.afterDatasetProjectCreated(datasetId);

      // NOTE we have an extra messabe box warning we catch, but we deactivated it. Normally it required another 'continue' button click (to 'cont'). We bypass it now.
      await this.cont();
      this.form = {
        file: null,
        sheets: null,
        selectedSheet: null,
        selectedCodebookSheet: null,
      };
      this.isLoading = false;
    },
    datasetProjectCreationError() {
      if (!this.validate()) return;
    },

    /***********
     *
     * File upload or edit methods
     *
     ***********/
    async codebookReuploaded(response) {
      if (response) {
        if (response.non_empty_question_text_count > 0) {
          this.datasetProject.no_codebook_matches = false;
          await this.saveDatasetProject(this.datasetProject);
        }
        this.setDataset(response.data_set_object);
        this.isShowingReupload = false;
        this.isCodebookErr = false;
        this.$router.push({
          name: 'dataset',
          params: { id: this.datasetProject.dataset_id },
          query: {
            project_id: this.datasetProject.id,
            from: 'datasetUploader',
          },
        });
      }
    },
    /**
     * When an excel is uploaded there are two cases to consider
     * 1. multi-sheet: upload is canceled, the excel sheets are returned
     * 2. singel-sheet: upload is sucess and the dataset id is returned
     */
    async processExcel() {
      this.isLoading = true;
      const response = await this.uploadDataset();
      if (response.sheet_names) {
        this.form.sheets = response.sheet_names;
        // auto select if it follows template
        if (this.form.sheets.some((sheet) => ['Data Sheet'].includes(sheet))) {
          this.form.selectedSheet = 'Data Sheet';
        }
        if (this.form.sheets.some((sheet) => ['Codebook Sheet'].includes(sheet))) {
          this.form.selectedCodebookSheet = 'Codebook Sheet';
        }
        this.isExcelMultiSheetUploaded = true;
      } else {
        const dataset = await this.$services.DATASETS_SERVICE.dataset(response.data_set_id.$oid);
        await this.setDataset(dataset);
        this.$refs['create-btn'].click();
      }
      this.isLoading = false;
    },
    async processCSV() {
      this.isLoading = true;
      const response = await this.uploadDataset();
      if (response.allDelimiters && response.allEncodings) {
        this.form.delimiters = response.allDelimiters;
        // auto select if it follows template
        if (response.delimiter) {
          this.form.delimiter = response.delimiter;
        }
        this.form.encodings = response.allEncodings;
        if (response.encoding) {
          this.form.encoding = response.encoding;
        }
        this.isCSVUploaded = true;
      } else {
        const dataset = await this.$services.DATASETS_SERVICE.dataset(response.data_set_id.$oid);
        this.setDataset(dataset);
      }
      this.isLoading = false;
    },
    async uploadDataset() {
      let formData = new FormData();
      formData.append('file', this.form.file);
      formData.append('name', this.datasetProject.name);
      if (this.form.selectedSheet) formData.append('sheet_name', this.form.selectedSheet);
      if (this.form.selectedCodebookSheet) {
        this.isCodebookUploaded = true;
        formData.append('code_book', this.form.file);
        formData.append('code_book_sheet_name', this.form.selectedCodebookSheet);
      }
      if (this.form.delimiter) {
        formData.append('use_delimiter', this.form.delimiter);
      }
      if (this.form.encoding) {
        formData.append('use_encoding', this.form.encoding);
      }

      try {
        this.isLoading = true;
        const response = await this.$services.DATASETS_SERVICE.upload(formData);
        this.isLoading = false;
        return response.data;
      } catch (e) {
        this.setDeleteIncompleteUpload(this.currDatasetProjectId);
        alert('There was an error uploading the file. Please try again');
        throw new Error('DatasetUploader:upload ' + e.message);
      }
    },
    async edit() {
      try {
        const response = await this.editDataset();
        this.setDataset(response.data_set_object);
        return response;
      } catch (e) {
        alert('There was an error uploading the file. Please try again');
        throw new Error('DatasetUploader:edit ' + e.message);
      }
    },
    async afterDatasetProjectCreated(datasetId) {
      this.isLoading = true;
      // update project status and add dataset id
      this.datasetProject.updateStatus('datasetUploadingCompleted');
      this.datasetProject.dataset_id = datasetId;
      await this.saveDatasetProject(this.datasetProject);

      // codebook matches were not found
      if (
        !this.isCodebookUploaded ||
        (this.uploadResponse && this.uploadResponse.non_empty_question_text_count == 0)
      ) {
        this.isCodebookErr = true;
        this.datasetProject.no_codebook_matches = true;
        this.saveDatasetProject(this.datasetProject);
        this.setDataset(await this.$services.DATASETS_SERVICE.dataset(datasetId));
        this.isLoading = false;
        return;
      }

      // update route to inprogress project route
      this.clearErrors();
      this.isLoading = false;
      this.$router.push({
        name: 'dataset',
        params: { id: this.datasetProject.dataset_id },
        query: { project_id: this.datasetProject.id, from: 'datasetUploader' },
      });
    },

    /***********
     *
     * Navigation buttons
     *
     ***********/
    async cont() {
      if (!this.permissions.includes('update-dataset') || this.readOnly) {
        this.setActiveSection('DatasetCleaner');
        return;
      }

      // if there was any changes to the dataset name, update the project
      if (this.isDataChanged && this.datasetWizardStatus.datasetUploadingComplete) {
        await this.edit();
        this.saveDatasetProject(this.datasetProject);
        this.$emit('datasetNameChanged');
      }
      // if they are ignoring the codebook matches error
      if (this.uploadResponse && this.isCodebookErr) {
        this.$router.push({
          name: 'dataset',
          params: { id: this.datasetProject.dataset_id },
          query: {
            project_id: this.datasetProject.id,
            from: 'datasetUploader',
          },
        });
      } else this.setActiveSection('DatasetCleaner');
    },
    // for text AI status checking
    incrementCounter() {
      this.counter++;
    },
    startTimer() {
      this.timer = setTimeout(
        function repeat() {
          this.isLoading = false;
          this.checkAllAIProcessingMixin();
          this.incrementCounter();
          this.timer = setTimeout(repeat.bind(this), 2000); // Adjust interval as needed
          if (this.textAIStatus == 'complete' || this.textAIStatus == 'error') {
            this.stopTimer();
          }
        }.bind(this),
        1000,
      );
    },
    stopTimer() {
      clearTimeout(this.timer);
    },
  },
  watch: {
    textAIStatus: {
      immediate: true,
      handler: function (status) {
        if (status && (status == 'complete' || status == 'error')) {
          this.stopTimer();
        }
        if (status && status == 'processing') {
          this.startTimer();
        }
      },
    },
  },
};
